import { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from '@/hooks/use-user';
import {
  ExternalCatalogConnectionCms,
  StoreRegion,
  GetDealByIdDocument,
} from '@/lib/graphql/generated/graphql';
import {
  setMixpanelGroup,
  setMixpanelGroupProfile,
  trackEvent,
} from '@/lib/mixpanel';
import { useLazyQuery } from '@apollo/client/react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { dealActiveVersionState } from '../../states/deal-active-version.state';
import { dealStateState } from '../../states/deal-state.state';
import {
  dealAtom,
  dealErrorAtom,
  dealLoadedForIdAtom,
  dealRefetchAtom,
} from '../../states/deal.state';
import { editionModeState } from '../../states/edition-mode.state';
import { hasCouponCodesFileState } from '../../states/has-coupon-codes-file.state';
import { pricingModeState } from '../../states/pricing-mode.state';
import { hasActiveShopifyConnectionAtom } from '../../states/shopify-connection.state';
import { storeAtom } from '../../states/store.state';
import { Store } from '../../types/store';

export const useDeal = (dealId: string) => {
  const setDeal = useSetAtom(dealAtom);
  const setDealError = useSetAtom(dealErrorAtom);
  const setLoadedForId = useSetAtom(dealLoadedForIdAtom);
  const setRefetch = useSetAtom(dealRefetchAtom);
  const setStore = useSetAtom(storeAtom);
  const setHasCouponCodesFile = useSetAtom(hasCouponCodesFileState);
  const setEditionMode = useSetAtom(editionModeState);
  const setDealState = useSetAtom(dealStateState);
  const setPricingMode = useSetAtom(pricingModeState);
  const setActiveVersion = useSetAtom(dealActiveVersionState);
  const setHasActiveShopifyConnection = useSetAtom(
    hasActiveShopifyConnectionAtom
  );
  const [hasResolvedInitialDealQuery, setHasResolvedInitialDealQuery] =
    useState(false);

  const [executeFetch, { data, loading, error }] = useLazyQuery(
    GetDealByIdDocument,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const jotaiStore = useStore();
  const user = useUser();
  // Treat a missing/undefined `isInternalUser` claim as a brand user, matching
  // `composeUser`/`resolveUserType` (undefined → brand). Guard on `user` so we
  // never group before identification has loaded.
  const isBrandUser = !!user && !user.isInternalUser;
  const trackedDealViewedForIdRef = useRef<string | null>(null);
  const brandGroupSetForRef = useRef<string | null>(null);

  // Initial fetch: skip if we already have data for this dealId (navigation remount).
  // Clear atom first when navigating to a different deal to avoid showing stale data.
  useEffect(() => {
    if (!dealId) return;

    const loadedForId = jotaiStore.get(dealLoadedForIdAtom);
    const hasDeal = jotaiStore.get(dealAtom) !== null;

    if (loadedForId !== dealId) {
      setDeal(null);
      setDealError(undefined);
      void executeFetch({ variables: { dealId } });
    } else if (!hasDeal) {
      void executeFetch({ variables: { dealId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Store refetch in atom so other hooks can trigger a refresh
  useEffect(() => {
    if (!dealId) return;
    setRefetch({
      fn: async () => {
        await executeFetch({ variables: { dealId } });
      },
    });
  }, [dealId, executeFetch, setRefetch]);

  // Sync error atom so consumers (e.g. useLatestPreviousCampaigns) can distinguish loading from failed
  useEffect(() => {
    setDealError(error);
  }, [error, setDealError]);

  // Sync atom when new data arrives
  useEffect(() => {
    if (data?.deal) {
      setDeal(data.deal);
      setLoadedForId(dealId);
      setActiveVersion(data.deal.activeVersion);
      // Convert StoreRegion (from backend) to Store (frontend enum)
      // StoreRegion uses Fr/Us, Store uses FR/US - same values, different casing
      const storeValue =
        data.deal.hubspotInfos.store === StoreRegion.Fr ? Store.FR : Store.US;
      setStore(storeValue);
      setHasCouponCodesFile(data.deal.hasCouponCodesFile ?? false);
      setEditionMode(data.deal.editionMode);
      setDealState(data.deal.state);
      setPricingMode(data.deal.pricingMode);
      setHasActiveShopifyConnection(
        (data.deal.externalCatalogConnections ?? []).some(
          (connection) =>
            connection?.cms === ExternalCatalogConnectionCms.Shopify &&
            connection?.isActive
        )
      );

      if (trackedDealViewedForIdRef.current !== dealId) {
        trackedDealViewedForIdRef.current = dealId;
        trackEvent('DEAL_VIEWED', { dealId });
        // Describe the offer entity on its group profile. `operationType` is a
        // per-deal typology (stable for a given deal).
        setMixpanelGroupProfile('dealId', dealId, {
          operationType: data.deal.hubspotInfos.operationType,
        });
      }

      // Group Analytics: link brand users to their brand so Mixpanel can
      // aggregate per brand (e.g. company health, which brands engage). Only
      // for brand users — tagging Choose would wrongly attribute their
      // cross-brand work to each brand. `brandId` is derived from the deal (a
      // brand user only ever works their own brand's deals). Set once per
      // brand; not retroactive. See docs/tracing-plan.md.
      const brandId = data.deal.brandId;
      if (isBrandUser && brandId && brandGroupSetForRef.current !== brandId) {
        brandGroupSetForRef.current = brandId;
        setMixpanelGroup('brandId', brandId);
        // Describe the brand entity on its group profile. tiering/legalStatus
        // are slowly-changing attributes → the profile keeps the latest value.
        setMixpanelGroupProfile('brandId', brandId, {
          brandTiering: data.deal.hubspotInfos.tiering,
          legalStatus: data.deal.legalStatus,
        });
      }
    }
  }, [
    data,
    dealId,
    isBrandUser,
    setDeal,
    setLoadedForId,
    setStore,
    setHasCouponCodesFile,
    setEditionMode,
    setDealState,
    setPricingMode,
    setActiveVersion,
    setHasActiveShopifyConnection,
  ]);

  const atomDeal = useAtomValue(dealAtom);
  const loadedId = useAtomValue(dealLoadedForIdAtom);
  // Don't expose stale deal data from a different dealId (avoids one-render flash on navigation)
  const deal = loadedId === dealId ? atomDeal : null;

  useEffect(() => {
    if (loading) {
      setHasResolvedInitialDealQuery(false);
      return;
    }
    const timer = setTimeout(() => {
      setHasResolvedInitialDealQuery(true);
    }, 2_000);
    return () => clearTimeout(timer);
  }, [loading, dealId]);

  const initialLoading = dealId
    ? !hasResolvedInitialDealQuery && !deal && !error
    : false;

  return {
    deal,
    loading,
    error,
    refetch: useCallback(
      () => executeFetch({ variables: { dealId } }),
      [executeFetch, dealId]
    ),
    initialLoading,
  };
};
